/**
 * Structured API error reporting using Sentry SDK
 * Provides better error grouping, context organization, and debugging information
 */
import { isAbortError } from '~/utility/isAbortError'

/**
 * Report API errors with structured context and proper Sentry grouping
 *
 * @param error - The error object (should have getContext() method for custom errors)
 * @param operation - Human-readable operation description (e.g., "calendar availability fetch")
 * @param additionalContext - Extra context specific to the operation (e.g., cacheKey, userId)
 *
 * IMPORTANT: The error's endpoint field should be a clean identifier (e.g., "calendar-availability")
 * NOT a URL with parameters (e.g., "/api/calendar?date=2025-01-01") to ensure proper Sentry grouping
 */
export const reportApiError = (
  error: any,
  operation: string,
  additionalContext: Record<string, any> = {},
) => {
  // Filter out AbortErrors from navigation-based request cancellation
  if (isAbortError(error)) {
    return
  }

  // Filter out 401 errors from spider/crawler requests
  // These are expected when crawlers hit authenticated endpoints
  const context = error?.getContext ? error.getContext() : {}
  const httpStatus = context.statusCode || context.httpStatusCode
  if (httpStatus === 401) {
    const userAgent =
      (typeof navigator !== 'undefined' ? navigator.userAgent : '') || ''
    const isSpider =
      /bot|crawler|spider|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|applebot/i.test(
        userAgent,
      )
    if (isSpider) {
      return // Don't report 401 from spiders - expected behavior
    }
  }

  // Filter out ALL 403 errors - likely Cloudflare bot blocks
  // These create high-volume Sentry noise without actionable value
  // Real user authorization errors should use 401 Unauthorized
  if (httpStatus === 403) {
    // Still log to console in dev for debugging
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[403 Forbidden - not reported to Sentry]:`, {
        endpoint: context.endpoint,
        url: context.url,
      })
    }
    return // Don't report any 403s to Sentry
  }

  const { $sentry } = useNuxtApp()

  // Guard clause: if $sentry is unavailable, fall back to console.error
  if (!$sentry) {
    console.error(`[${operation}]:`, {
      message: error?.message,
      ...additionalContext,
      ...context,
    })
    return
  }

  // In development, still use console.error for immediate debugging visibility
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${operation}]:`, {
      message: error?.message,
      ...additionalContext,
      ...context,
    })
    return
  }

  // Set structured context for filtering and organization
  $sentry.setContext('apiError', {
    operation,
    endpoint: context.endpoint || 'unknown',
    isTimeout: context.isTimeout || false,
    statusCode: context.statusCode,
    httpStatusCode: context.httpStatusCode,
    url: context.url,
    // Enhanced network diagnostics
    isNetworkError: context.isNetworkError,
    errorKind: context.errorKind,
    possibleCauses: context.possibleCauses,
    ...additionalContext, // Include operation-specific context like cacheKey
  })

  // Set searchable tags for filtering in Sentry
  $sentry.setTag('errorCategory', 'api')
  $sentry.setTag('errorType', error.name || 'UnknownError')
  $sentry.setTag('apiEndpoint', context.endpoint || 'unknown')
  $sentry.setTag(
    'operation',
    operation.replace(/[^a-zA-Z0-9-]/g, '-').toLowerCase(),
  )

  // Set timeout-specific tag for easy filtering
  if (context.isTimeout) {
    $sentry.setTag('isTimeout', 'true')
  }

  // Set network error classification tags for easy filtering
  if (context.errorKind) {
    $sentry.setTag('errorKind', context.errorKind)
  }
  if (context.isNetworkError) {
    $sentry.setTag('isNetworkError', 'true')
  }

  // Set validation error tag for filtering and analysis (especially useful for review submissions)
  if (additionalContext.validationError) {
    $sentry.setTag('validationError', additionalContext.validationError)
  }

  // Add detailed debugging data as extra context (not searchable but visible in issues)
  if (context.params) {
    $sentry.setExtra('requestParams', context.params)
  }
  if (context.rawResponse) {
    $sentry.setExtra('rawResponse', context.rawResponse)
  }
  if (context.validationErrors) {
    $sentry.setExtra('validationErrors', context.validationErrors)
  }
  if (context.stack) {
    $sentry.setExtra('fullStackTrace', context.stack)
  }

  // Enhanced network diagnostics for "Failed to fetch" debugging
  if (context.navigator) {
    $sentry.setExtra('navigator', context.navigator)
  }
  if (context.connection) {
    $sentry.setExtra('connection', context.connection)
  }
  if (context.page) {
    $sentry.setExtra('page', context.page)
  }
  if (context.requestTiming) {
    $sentry.setExtra('requestTiming', context.requestTiming)
  }
  if (context.debuggingHints) {
    $sentry.setExtra('debuggingHints', context.debuggingHints)
  }
  if (context.originalErrorName) {
    $sentry.setExtra('originalErrorName', context.originalErrorName)
  }
  if (context.originalErrorStack) {
    $sentry.setExtra('originalErrorStack', context.originalErrorStack)
  }

  // Add operation-specific extra context
  Object.keys(additionalContext).forEach((key) => {
    $sentry.setExtra(key, additionalContext[key])
  })

  // Capture with custom fingerprinting for precise error grouping
  // This ensures similar errors group together regardless of variable data
  const fingerprint = [
    error.name || 'UnknownError',
    context.endpoint || 'unknown',
    context.isTimeout
      ? 'timeout'
      : context.statusCode
        ? `status-${context.statusCode}`
        : 'failed',
  ]

  $sentry.captureException(error, {
    fingerprint,
    level: context.isTimeout ? 'warning' : 'error',
    tags: {
      // Additional context-specific tags can be added here
      errorCategory: 'api',
    },
  })
}

/**
 * Report general application errors (non-API)
 *
 * @param error - The error object
 * @param context - Descriptive context about what was happening
 * @param additionalData - Any relevant debugging data
 */
export const reportAppError = (
  error: any,
  context: string,
  additionalData: Record<string, any> = {},
) => {
  // Filter out AbortErrors from navigation-based request cancellation
  if (isAbortError(error)) {
    return
  }

  const { $sentry } = useNuxtApp()

  // Guard clause: if $sentry is unavailable, fall back to console.error
  if (!$sentry) {
    console.error(`[${context}]:`, {
      message: error?.message,
      ...additionalData,
    })
    return
  }

  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]:`, {
      message: error.message,
      ...additionalData,
    })
    return
  }

  // Set structured context for filtering and organization
  $sentry.setContext('appError', {
    context,
    ...additionalData,
  })

  // Set searchable tags for filtering
  $sentry.setTag('errorCategory', 'application')
  $sentry.setTag('errorType', error.name || 'UnknownError')

  // Add debugging data as extra context
  Object.keys(additionalData).forEach((key) => {
    $sentry.setExtra(key, additionalData[key])
  })

  $sentry.captureException(error, {
    fingerprint: [error.name || 'UnknownError', context],
    level: 'error',
  })
}
